// Presentación de la cobranza (recordatorios de pago a comercios). Funciones puras: se
// testean sin montar componentes.
//
// Los textos son claves i18n (el español ES la clave): los componentes los pasan por t().
// Billing es la autoridad de todo lo que se decide acá (qué es cobrable, el enfriamiento de
// 24 h, la hora local): el front solo lo adelanta para explicar un botón deshabilitado o un
// campo inválido antes de que vuelva un 400/409.

import { hasAmountToCollect } from './cobroHelpers';
import { apiBodyMessage } from '../../../../utils/apiErrorMessage';

// ── Canales y disparadores ──────────────────────────────────────────────────────────────

export const REMINDER_CHANNELS = ['email', 'whatsapp'];

export const CHANNEL_LABELS = {
  email: 'Correo',
  whatsapp: 'WhatsApp',
};

export const TRIGGER_LABELS = {
  auto: 'Automático',
  scheduled: 'Programado',
  manual: 'Manual',
};

/** Canales elegidos de un `{ email, whatsapp }`, en orden fijo. */
export const selectedChannels = (channels) =>
  REMINDER_CHANNELS.filter((c) => channels?.[c] === true);

// ── Estado de cada envío (fila de `dispatches`) ─────────────────────────────────────────

export const DISPATCH_STATUSES = [
  'scheduled',
  'reserved',
  'sent',
  'retryable',
  'failed',
  'skipped',
  'unknown',
  'cancelled',
];

export const DISPATCH_STATUS_LABELS = {
  scheduled: 'Programado',
  reserved: 'Enviando',
  sent: 'Enviado',
  retryable: 'Reintentando',
  failed: 'Falló',
  skipped: 'Omitido',
  unknown: 'Resultado incierto',
  cancelled: 'Cancelado',
};

export const DISPATCH_STATUS_TONE = {
  scheduled: 'info',
  reserved: 'info',
  sent: 'success',
  retryable: 'warning',
  failed: 'error',
  skipped: 'neutral',
  unknown: 'warning',
  cancelled: 'neutral',
};

// Aclaración de los estados que no se explican solos (se muestra como title del badge).
export const DISPATCH_STATUS_HINTS = {
  // El enfriamiento de 24 h cuenta el intento dudoso como enviado: hasta entonces billing
  // rechaza el reenvío manual por ese canal (409 cooldown).
  unknown: 'Pudo haber llegado. No se reintenta solo; se podrá reenviar a mano pasadas 24 h desde el intento.',
  retryable: 'No se envió todavía: se reintenta solo en un próximo ciclo.',
};

// ── Códigos de error de un envío (cerrados en billing; nunca el cuerpo del proveedor) ────

export const REMINDER_ERROR_LABELS = {
  // Sin enviar, se reintenta solo.
  contacts_unavailable: 'No se pudieron leer los contactos del comercio.',
  provider_unreachable: 'Proveedor no disponible.',
  provider_auth_failed: 'Credencial de correo inválida.',
  config_unauthorized: 'Clave de WhatsApp inválida.',
  config_not_found: 'URL de WhatsApp mal configurada.',
  rate_limited: 'Límite de envío del número alcanzado.',
  instance_not_connected: 'Número de WhatsApp desconectado.',
  idempotency_unavailable: 'Servicio de WhatsApp no disponible.',
  evolution_unavailable: 'Servicio de WhatsApp no disponible.',
  temporarily_unavailable: 'Servicio de WhatsApp no disponible.',
  in_progress: 'Envío en curso.',
  timeout: 'Sin respuesta: se reintenta con la misma clave, sin duplicar.',
  // Definitivos.
  no_recipients: 'Sin destinatarios para el canal.',
  no_country: 'No se pudo determinar el país del teléfono.',
  invalid_phone: 'Teléfono inválido para WhatsApp.',
  no_integration: 'Sin número de WhatsApp.',
  integration_not_found: 'El número elegido ya no existe o es de otro operador.',
  recipient_not_on_whatsapp: 'El teléfono no tiene WhatsApp.',
  invalid_request: 'El servicio de WhatsApp rechazó la solicitud.',
  email_rejected: 'El proveedor de correo rechazó el mensaje.',
  // El WhatsApp armado pasaba del tope y no se envió (solo con la plantilla del sistema y
  // nombres desmesurados: las plantillas guardadas se validan contra el peor caso).
  message_too_long: 'El WhatsApp pasaba del máximo de 4096 caracteres y no se envió.',
  // Ambiguo: no se reintenta solo.
  outcome_unknown: 'No se sabe si llegó.',
  // Omitidos por una regla.
  not_collectible: 'La nota ya no es cobrable.',
  paused: 'Automáticos pausados para el comercio.',
  cooldown: 'Ya se envió uno en las últimas 24 h.',
  auto_disabled: 'Se descartó porque la regla automática se apagó o cambió de canal o de número.',
  // Del propio envío.
  reservation_expired: 'Envío interrumpido por un reinicio.',
  retry_window_expired: 'Se agotaron los reintentos (48 h).',
  expired: 'No salió a tiempo y se descartó para no enviarlo tarde.',
  operator_quota: 'Se alcanzó el tope diario de correos del operador.',
};

/**
 * Clave i18n del motivo de un `errorCode`, o `null` si no hay código o es uno que esta
 * versión del front no conoce (billing puede sumar códigos: el componente muestra el código
 * crudo en vez de inventar un motivo).
 */
export const reminderErrorLabel = (code) =>
  (code && Object.prototype.hasOwnProperty.call(REMINDER_ERROR_LABELS, code)
    ? REMINDER_ERROR_LABELS[code]
    : null);

// ── Qué notas admiten recordatorios ─────────────────────────────────────────────────────

// Mismo conjunto que billing (`COLLECTIBLE_PAYMENT_STATUSES`): `in_review` queda afuera
// porque el comercio ya informó un pago.
const COLLECTIBLE_PAYMENT_STATUSES = ['unpaid', 'partially_paid', 'overdue'];

/**
 * ¿La nota admite recordatorios? Emitida, impaga o con pago parcial, y con monto a cobrar
 * (mismo criterio que `isCollectible` de billing: un total 0 no es cobrable).
 */
export const isCollectibleNote = (note) =>
  !!note &&
  note.status === 'issued' &&
  COLLECTIBLE_PAYMENT_STATUSES.includes(note.paymentStatus) &&
  hasAmountToCollect(note);

// ── Enfriamiento de 24 h por comercio y canal ───────────────────────────────────────────

export const REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const toTime = (value) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// Instante que cuenta para el enfriamiento de una fila, o null si no cuenta. Mismo criterio
// que billing: lo enviado por su `sentAt`; lo en curso o dudoso (reserved/retryable/unknown)
// como si hubiera salido. La vista no expone `reservedAt`, así que se usa `updatedAt` (la
// reserva es la última escritura de esas filas). Un "enviar ahora" recién aceptado
// (manual + scheduled) sale en segundos: también cuenta, para no ofrecer un segundo envío.
const activityTime = (row) => {
  if (row?.status === 'sent') return toTime(row.sentAt);
  if (['reserved', 'retryable', 'unknown'].includes(row?.status)) return toTime(row.updatedAt);
  if (row?.status === 'scheduled' && row.trigger === 'manual') return toTime(row.createdAt);
  return null;
};

/**
 * Enfriamiento vigente por canal a partir del historial reciente del COMERCIO (billing
 * limita a un recordatorio por comercio y canal cada 24 h, de cualquier origen).
 *
 * Es orientativo (reloj del navegador, filas cargadas): billing responde 409 igual.
 *
 * @param {Array<object>} dispatches filas de `GET dispatches?companyId=`
 * @param {Date} [now]
 * @returns {{ email: { lastActivityAt: Date, remainingMs: number } | null,
 *             whatsapp: { lastActivityAt: Date, remainingMs: number } | null }}
 */
export function cooldownByChannel(dispatches, now = new Date()) {
  const nowMs = now.getTime();
  const result = { email: null, whatsapp: null };
  for (const row of Array.isArray(dispatches) ? dispatches : []) {
    if (!REMINDER_CHANNELS.includes(row?.channel)) continue;
    const at = activityTime(row);
    if (at === null) continue;
    const remainingMs = at + REMINDER_COOLDOWN_MS - nowMs;
    if (remainingMs <= 0) continue;
    const current = result[row.channel];
    if (!current || at > current.lastActivityAt.getTime()) {
      result[row.channel] = { lastActivityAt: new Date(at), remainingMs };
    }
  }
  return result;
}

/** Horas enteras hacia arriba (para "se podrá reenviar en N h"). Mínimo 1. */
export const hoursCeil = (ms) => Math.max(1, Math.ceil(Number(ms) / (60 * 60 * 1000)));

// ── Hora local del operador ─────────────────────────────────────────────────────────────
//
// Billing interpreta la programación como día y hora LOCALES del operador ({date, hour}) y
// devuelve sus horas locales como strings. El front nunca las pasa por `new Date(...)`, que
// las leería en el huso del navegador.

/** Hora de inicio por defecto de un envío programado para mañana (la misma de billing). */
export const DEFAULT_SEND_HOUR = 10;
/** Última hora que se propone por defecto (el mismo tope que la hora de la regla automática). */
export const LAST_DEFAULT_SEND_HOUR = 19;
/**
 * Horario habitual [desde, hasta): un programado fuera de él se permite (billing acepta
 * 0..23), pero se avisa. De noche el mensaje sale desde el número del operador.
 */
export const USUAL_SEND_HOURS = { from: 8, to: 20 };

/** ¿La hora (0..23) cae fuera del horario habitual? */
export const isOutsideUsualSendHours = (hour) =>
  Number(hour) < USUAL_SEND_HOURS.from || Number(hour) >= USUAL_SEND_HOURS.to;

/** Tope de anticipación de un envío programado (billing responde 400 más allá). */
export const MAX_SCHEDULE_DAYS_AHEAD = 90;

/** El huso si Intl lo conoce; si no, `undefined` (formatea en el huso del navegador). */
export function safeTimeZone(timeZone) {
  if (!timeZone) return undefined;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch (err) {
    if (err instanceof RangeError) return undefined;
    throw err;
  }
}

const partsIn = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    // Algunos motores devuelven '24' a la medianoche aun con h23.
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
  };
};

/** Día ('YYYY-MM-DD') y hora (0..23) de `now` en el huso del operador. */
export function localNowParts(timeZone, now = new Date()) {
  const { date, hour } = partsIn(now, timeZone);
  return { date, hour };
}

/** 'YYYY-MM-DD' + n días, sin husos (aritmética de calendario). */
export function addDaysToDayKey(dayKey, days) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey ?? ''));
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * Fecha y hora por defecto de un envío programado: la próxima hora en punto del operador,
 * siempre entre DEFAULT_SEND_HOUR y LAST_DEFAULT_SEND_HOUR. De madrugada propone hoy a las
 * DEFAULT_SEND_HOUR; pasada la última, mañana a esa hora. Un operador que programa de
 * noche y confirma sin mirar no dispara mensajes nocturnos.
 */
export function defaultScheduleSendAt(timeZone, now = new Date()) {
  const { date, hour } = localNowParts(timeZone, now);
  const next = hour + 1;
  if (next < DEFAULT_SEND_HOUR) return { date, hour: DEFAULT_SEND_HOUR };
  if (next <= LAST_DEFAULT_SEND_HOUR) return { date, hour: next };
  return { date: addDaysToDayKey(date, 1), hour: DEFAULT_SEND_HOUR };
}

/**
 * Validación previa de `{date, hour}` contra el reloj del operador. Devuelve la clave i18n
 * del error o null. La hora en curso se acepta (billing la manda en el próximo ciclo).
 */
export function scheduleSendAtError(sendAt, timeZone, now = new Date()) {
  const date = String(sendAt?.date ?? '');
  const hour = Number(sendAt?.hour);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || addDaysToDayKey(date, 0) !== date) {
    return 'La fecha de envío no existe.';
  }
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return 'Elegí una hora entre las 00 y las 23.';
  const today = localNowParts(timeZone, now);
  if (date < today.date || (date === today.date && hour < today.hour)) {
    return 'La fecha y hora de envío ya pasaron.';
  }
  if (date > addDaysToDayKey(today.date, MAX_SCHEDULE_DAYS_AHEAD)) {
    return 'El envío se puede programar hasta 90 días hacia adelante.';
  }
  return null;
}

const pad2 = (n) => String(n).padStart(2, '0');

/** 0..23 → 'HH:00'. */
export const formatHour = (hour) => `${pad2(Number(hour) || 0)}:00`;

/**
 * Hora local de billing ('YYYY-MM-DDTHH:mm' o 'YYYY-MM-DDTHH:00') → 'dd-mm-aaaa HH:mm',
 * recortando el string. '' si no tiene esa forma.
 */
export function formatLocalDateTime(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(value ?? ''));
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` : '';
}

/**
 * Fechas locales ('dd-mm-aaaa HH:mm'), en orden y sin repetir, de los programados donde ya
 * esperaban las nota+canal que billing omitió al programar (`alreadyScheduled` de POST
 * schedules). Vacío si billing no manda `sendAtLocal` en esos elementos.
 *
 * @param {Array<{ sendAtLocal?: string }>} alreadyScheduled
 * @returns {string[]}
 */
export function alreadyScheduledDates(alreadyScheduled) {
  const raw = (Array.isArray(alreadyScheduled) ? alreadyScheduled : [])
    .map((item) => item?.sendAtLocal)
    .filter((value) => typeof value === 'string');
  // 'YYYY-MM-DDTHH:mm' ordena como texto; se deduplica ya formateado.
  return [...new Set(raw.sort().map(formatLocalDateTime).filter(Boolean))];
}

/** Instante (ISO) → 'dd-mm-aaaa HH:mm' en el huso del operador. '' si no es válido. */
export function formatInstant(value, timeZone) {
  const ms = toTime(value);
  if (ms === null) return '';
  const { date, hour, minute } = partsIn(new Date(ms), timeZone);
  const [y, mo, d] = date.split('-');
  return `${d}-${mo}-${y} ${pad2(hour)}:${pad2(minute)}`;
}

// ── Números de WhatsApp del operador ────────────────────────────────────────────────────

export const WA_CONNECTION_LABELS = {
  open: 'Conectado',
  connecting: 'Conectando',
  close: 'Desconectado',
  degraded: 'Inestable',
};

export const WA_CONNECTION_TONE = {
  open: 'success',
  connecting: 'warning',
  close: 'error',
  degraded: 'warning',
};

export const WA_PURPOSE_LABELS = {
  both: 'Ambos',
  notifications: 'Notificaciones',
  helpdesk: 'Mesa de ayuda',
};

const WHATSAPP_UNOFFICIAL = 'whatsapp_unofficial';

/** Id de una integración tal como la devuelve conversations (`id` o `_id`). */
export const integrationIdOf = (integration) => String(integration?.id ?? integration?._id ?? '');

/**
 * Opciones del selector de número de WhatsApp.
 *
 * Se listan TODOS los números no oficiales que no estén inactivos, no solo los conectados:
 * si el guardado desaparece porque su sesión se cortó, el selector se ve vacío y el
 * operador cree que no hay número o lo cambia sin querer. Solo los activos y conectados
 * (`open`) son elegibles para una selección NUEVA; el guardado se conserva igual, con aviso.
 *
 * @param {Array<object>} integrations respuesta de GET channel-integrations
 * @param {string|null} savedId número guardado en la configuración de cobranza
 * @returns {Array<{ id: string, label: string, phone: string|null, connection: string,
 *   purpose: string, usable: boolean, isSaved: boolean, selectable: boolean }>}
 */
export function whatsappNumberOptions(integrations, savedId) {
  const saved = savedId ? String(savedId) : null;
  return (Array.isArray(integrations) ? integrations : [])
    .filter((i) => (i?.channel ?? WHATSAPP_UNOFFICIAL) === WHATSAPP_UNOFFICIAL)
    .filter((i) => i?.status !== 'inactive')
    .map((i) => {
      const id = integrationIdOf(i);
      const connection = i.wa_connection_status || 'close';
      const usable = i.status === 'active' && connection === 'open';
      return {
        id,
        label: i.display_name || i.phone_number || id,
        phone: i.phone_number || null,
        connection,
        // Registros previos al campo se interpretan como 'both' (mismo criterio que la mesa).
        purpose: i.purpose || 'both',
        usable,
        isSaved: saved !== null && id === saved,
        selectable: usable || (saved !== null && id === saved),
      };
    })
    .filter((o) => o.id);
}

/**
 * Número por defecto para un envío puntual: el de la configuración si sigue listado. Si hay
 * uno configurado y no está (borrado o inactivo), `null`: no se elige otro en silencio,
 * porque las respuestas del comercio quedarían en el hilo de otro número (u ocultas, si es
 * de notificaciones). Sin número configurado, el único usable. `null` si hay que elegir.
 */
export function defaultWhatsappNumber(options, savedId) {
  const saved = savedId ? String(savedId) : '';
  if (saved) return options.some((o) => o.id === saved) ? saved : null;
  const usable = options.filter((o) => o.usable);
  return usable.length === 1 ? usable[0].id : null;
}

/** ¿Hay un número configurado que ya no aparece entre las opciones? */
export const isSavedWhatsappMissing = (options, savedId) =>
  !!savedId && !options.some((o) => o.id === String(savedId));

// ── Errores de la API de cobranza ───────────────────────────────────────────────────────

/** Mensaje de error de Nest (string o arreglo de class-validator), o null. */
export function apiErrorMessage(err) {
  const message = err?.response?.data?.message;
  return message === undefined ? null : apiBodyMessage({ message });
}

export const apiErrorStatus = (err) => err?.response?.status ?? null;

export const apiErrorCode = (err) => {
  const code = err?.response?.data?.code;
  return typeof code === 'string' ? code : null;
};

/**
 * ¿No se sabe si billing llegó a guardar el pedido? Sin respuesta (red, timeout del proxy) o
 * un 408/5xx sin código de billing. Un error con `code` es una decisión de billing, y un 4xx
 * es un rechazo: en esos casos no se guardó nada.
 */
export function isRequestOutcomeUnknown(err) {
  if (apiErrorCode(err)) return false;
  const status = apiErrorStatus(err);
  return status === null || status === 408 || status >= 500;
}

const ERROR_CODE_LABELS = {
  scheduler_disabled: 'El envío de recordatorios no está habilitado en este entorno.',
  channel_not_configured: 'El canal elegido no está configurado en este entorno.',
  not_collectible: 'La nota no admite recordatorios: está pagada, en revisión, anulada o sin monto a cobrar.',
  idempotency_key_reused:
    'Un intento anterior de este envío ya quedó registrado con otros datos. Revisá lo programado y el historial antes de reintentar: si volvés a enviar, se registra como un envío nuevo.',
  schedule_quota:
    'Hay demasiados recordatorios programados pendientes. Cancelá alguno en "Programados" o esperá a que salgan antes de programar más.',
  // Una nota consolidada de holding se coló en el envío (p. ej. una lista vieja): billing la
  // rechaza entera con `noteIds`.
  holding_reminders_not_supported:
    'Las notas de holdings todavía no admiten recordatorios: sacalas de la selección y volvé a intentar.',
};

/**
 * Texto para mostrar de un error de la API de cobranza. Los códigos conocidos se traducen;
 * el enfriamiento se arma con sus datos (canal y horas) y el tope de programados con los
 * suyos (`pending` y `limit`); el resto usa el mensaje de billing (ya en español y
 * específico) o, si no hay, `fallback`.
 *
 * @param {unknown} err error de axios
 * @param {(key: string, opts?: object) => string} t
 * @param {string} fallback clave i18n
 */
export function collectionsErrorText(err, t, fallback) {
  const code = apiErrorCode(err);
  if (code === 'schedule_quota') {
    const { pending, limit } = err?.response?.data ?? {};
    if (Number.isFinite(pending) && Number.isFinite(limit)) {
      return t(
        'Hay {{pending}} recordatorios programados pendientes y el máximo por operador es {{limit}}: quedan {{free}} lugares y este envío necesita más. Programá menos notas o canales, cancelá alguno en "Programados" o esperá a que salgan.',
        { pending, limit, free: Math.max(0, limit - pending) },
      );
    }
  }
  if (code === 'cooldown') {
    const blocked = Array.isArray(err?.response?.data?.cooldown) ? err.response.data.cooldown : [];
    if (blocked.length > 0) {
      return blocked
        .map((b) =>
          t('Ya se envió un recordatorio a este comercio por {{canal}} en las últimas 24 h. Se podrá reenviar en {{horas}} h.', {
            canal: t(CHANNEL_LABELS[b.channel] ?? String(b.channel)),
            horas: hoursCeil(Number(b.retryAfterSeconds) * 1000),
          }),
        )
        .join(' ');
    }
  }
  if (code && ERROR_CODE_LABELS[code]) return t(ERROR_CODE_LABELS[code]);
  if (apiErrorStatus(err) === 403) return t('Tu rol no tiene permiso para gestionar la cobranza.');
  return apiErrorMessage(err) ?? t(fallback);
}

// ── Idempotencia ────────────────────────────────────────────────────────────────────────

/**
 * uuid v4 para `idempotencyKey`. Se genera UNA vez por intención (al abrir el formulario) y
 * se reutiliza en los reintentos del mismo envío: billing devuelve lo ya creado en vez de
 * duplicar. `crypto.randomUUID` exige contexto seguro; `getRandomValues` no.
 */
export function newIdempotencyKey(
  cryptoImpl = typeof window !== 'undefined' ? window.crypto : undefined,
) {
  if (typeof cryptoImpl?.randomUUID === 'function') return cryptoImpl.randomUUID();
  if (typeof cryptoImpl?.getRandomValues !== 'function') {
    throw new Error('El navegador no ofrece un generador aleatorio seguro (crypto).');
  }
  const bytes = cryptoImpl.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // versión 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
